import React, { useMemo } from 'react';
import { Label } from '@fluentui/react';
import { HelpButton } from '@bingads-webui-react/help-popup';
import {
  Caption1Strong,
  Combobox,
  makeStyles,
  mergeClasses,
  Option,
  tokens,
  useId,
} from '@fluentui/react-components';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';

import { getCallToActionOptions, getCustomTextOption } from './helpers';

const useCallToActionStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  labelHeading: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
  },
  select: {
    width: '100%',
  },
  input: {
    width: '100%',
  },
  customTextContainer: {
    '& .text-input-container': {
      width: '100%',
    },
  },
});

export interface CallToActionProps {
  label?: React.ReactNode;
  cta?: string;
  ctaValidations?: {
    minLength: number;
    maxLength: number;
  };
  inlinePopup?: boolean;
  placeholder?: string;
  labelAction?: React.ReactNode;
  withCustomText?: boolean;
  onChangeCta: (cta: string) => void;
  helpTopic?: string;
  helpLabel?: string;
  i18n: {
    getString: (key: string) => string;
  }
}

const defaultProps = {
  label: '',
  cta: '',
  ctaValidations: {
    minLength: 0,
    maxLength: 20,
  },
  inlinePopup: false,
  placeholder: '',
  labelAction: null,
  withCustomText: true,
  helpTopic: '',
  helpLabel: '',
  i18n: {
    getString: (key: string) => key,
  },
  onChangeCta: () => {},
} as CallToActionProps;

const CallToAction: React.FC<CallToActionProps> = ({
  label,
  cta,
  ctaValidations,
  inlinePopup,
  placeholder,
  labelAction,
  withCustomText,
  onChangeCta,
  helpTopic,
  helpLabel,
  i18n,
} = defaultProps) => {
  const classes = useCallToActionStyles();
  const ctaSelectLabelId = useId('cta-select-label');
  const customInputLabelId = useId('custom-cta-input-label');

  const customTextOption = getCustomTextOption(i18n);
  const availableOptions = getCallToActionOptions(i18n, withCustomText);

  const selectedCtaOption = useMemo(() => {
    if (!cta) return null;
    return availableOptions.find(option => option.value === cta);
  }, [availableOptions, cta]);

  const isCustomTextOptionSelected = withCustomText ? cta && (cta === customTextOption.value || !selectedCtaOption) : false;
  const ctaOption = isCustomTextOptionSelected ? customTextOption : selectedCtaOption;

  const [selectedOption, setSelectedOption] = React.useState<string>(ctaOption?.value || '');
  const [customInputText, setCustomInputText] = React.useState(isCustomTextOptionSelected ? cta : '');
  const isCustomOption = (isCustomTextOptionSelected || selectedOption === customTextOption.value);

  const handleSelect = (_ev: React.ChangeEvent<HTMLInputElement>, data: { optionText: string }) => {
    if (!data || !data.optionText) return;
    setSelectedOption(data.optionText);
    if (withCustomText) {
      onChangeCta(data.optionText === customTextOption.value ? customInputText || '' : data.optionText);
    } else {
      onChangeCta(data.optionText);
    }
  };

  const handleInput = ({ data }) => {
    setCustomInputText(data.callToAction);
  };

  const ctaSchema = useMemo(() => ({
    type: 'object',
    properties: {
      callToAction: {
        type: 'string',
        maxLength: ctaValidations?.maxLength,
        minLength: ctaValidations?.minLength || 0,
      },
    },
  }), [ctaValidations?.maxLength, ctaValidations?.minLength]);

  return (
    <div className={mergeClasses('display-ads-call-to-action', classes.root)}>
      <div className={mergeClasses('display-ads-call-to-action-label', classes.label)}>
        {label &&
        <Label id={ctaSelectLabelId} className={mergeClasses('display-ads-call-to-action-label-heading', classes.labelHeading)}>
          {label}
          {helpTopic && (
            <HelpButton
              topic={helpTopic}
              aria-label={helpLabel}
            />
          )}
        </Label>}
        {labelAction}
      </div>
      <Combobox
        inlinePopup={inlinePopup}
        placeholder={placeholder}
        aria-labelledby={ctaSelectLabelId}
        aria-label={i18n.getString(_TL_('Call to action'))}
        className={mergeClasses('display-ads-call-to-action-select', classes.select)}
        value={selectedOption}
        selectedOptions={[selectedOption]}
        // @ts-ignore callback args types are not exported
        onOptionSelect={handleSelect}
      >
        {availableOptions.map(option => (
          <Option
            key={option.key}
            value={option.value}
            aria-label={option.value}
            id={option.key.replace(/\s/g, '-').toLowerCase()}
          >
            {option.value}
          </Option>
        ))}
      </Combobox>
      {isCustomOption && (
        <div className={mergeClasses('display-ads-call-to-action-input', classes.customTextContainer)}>
          <RemainingCharsValidatedTextField
            // @ts-ignore value exists in the props but is not defined in the type
            value={customInputText}
            aria-labelledby={customInputLabelId}
            title={i18n.getString(_TL_('Custom action text'))}
            validationProps={{
              onValidate: handleInput,
              field: 'callToAction',
              schema: ctaSchema,
              alwaysShowError: true,
            }}
            onRenderLabel={() => (
              <Label aria-label="Custom action text" id={customInputLabelId}>
                <Caption1Strong>{i18n.getString(_TL_('Custom action text'))}</Caption1Strong>
              </Label>)}
            onBlur={() => customInputText && onChangeCta(customInputText)}
            i18n={i18n}
          />
        </div>
      )}
    </div>
  );
};

CallToAction.defaultProps = defaultProps;

export { CallToAction, getCallToActionOptions, getCustomTextOption };
